<template>
  <div class="mx-boardwrap" :class="{ 'is-dragging': !!dragged || touchDrag.active }">

    <div v-if="boardLoading" class="mx-empty">Cargando el tablero…</div>
    <div v-else-if="boardError" class="mx-empty">
      No se pudo cargar el tablero.<br>{{ boardError }}
      <br><button type="button" class="mx-act mx-act--line" style="margin-top:10px" @click="fetchBoard">Reintentar</button>
    </div>

    <div v-else class="mx-board" ref="board">
      <!-- ── Columnas de etapa ── -->
      <section
        v-for="(etapa, i) in etapas"
        :key="etapa.key"
        class="mx-pane mx-col"
        :class="{ 'is-over': dragOverEtapa === etapa.key }"
        :style="hueVars(etapa, i)"
        :data-etapa="etapa.key"
        @dragover.prevent="onDragOver(etapa.key, $event)"
        @dragleave="onDragLeave(etapa.key)"
        @drop.prevent="onDrop(etapa.key, $event)"
      >
        <header class="mx-col__h">
          <span class="mx-col__dot"></span>
          <span class="mx-col__id">
            <span class="mx-col__nm">
              {{ etapa.label }}
              <span v-if="etapa.pedir_razon_fallo" class="mx-col__ask" title="Pide un motivo al mover un chat acá">⚠</span>
            </span>
            <span class="mx-col__sub">
              <template v-if="columnFirstContactAvg(etapa)">1er contacto · visibles <b>{{ columnFirstContactAvg(etapa) }}</b></template>
              <template v-else>{{ visibleCards(etapa).length ? 'sin promedio' : 'sin conversaciones' }}</template>
            </span>
          </span>
          <span class="mx-col__n" :class="{ 'is-risk': columnHasRisk(etapa, i) }">
            {{ isColFiltered(etapa.key) ? visibleCards(etapa).length : columnCountLabel(etapa.key) }}
          </span>
          <span class="mx-col__tools">
            <button type="button" class="mx-col__tool" :class="{ 'is-on': colSearch.key === etapa.key }" title="Buscar en esta columna" @click.stop="toggleColSearch(etapa.key)">⌕</button>
            <button v-if="canManageStages && isSectionMode" type="button" class="mx-col__tool" title="Renombrar columna" @click.stop="openEditStage(etapa)">✎</button>
            <button v-if="canManageStages && isSectionMode" type="button" class="mx-col__tool is-danger" title="Eliminar columna" @click.stop="openDeleteStage(etapa)">🗑</button>
          </span>
        </header>

        <div v-if="colSearch.key === etapa.key" class="mx-col__find">
          <input ref="colSearchInput" v-model="colSearch.query" type="text" placeholder="Nombre o teléfono…" @keydown.esc="closeColSearch" />
          <button type="button" class="mx-col__tool" title="Cerrar" @click="closeColSearch">✕</button>
        </div>

        <div class="mx-col__b" @scroll="onColScroll(etapa.key, $event)">
          <article
            v-for="card in visibleCards(etapa)"
            :key="card.id"
            class="mx-card"
            :class="{
              'is-drag': isCardDragging(card),
              'is-picked': selectMode && isCardSelected(card.id)
            }"
            :draggable="!selectMode"
            @click="onCardClick(card)"
            @dragstart="onDragStart(card, etapa.key, $event)"
            @dragend="onDragEnd"
            @touchstart="onCardTouchStart(card, etapa.key, $event)"
            @touchmove="onCardTouchMove($event)"
            @touchend="onCardTouchEnd($event)"
            @touchcancel="onCardTouchCancel"
          >
            <span class="mx-card__bar" :class="riskClass(card, i)"></span>
            <span v-if="selectMode" class="mx-card__ck" :class="{ 'is-on': isCardSelected(card.id) }">✓</span>
            <span v-else class="mx-card__av">{{ initials(card.client_name || card.client_identifier) }}</span>

            <span class="mx-card__head">
              <span class="mx-card__l1">
                <span class="mx-card__nm">{{ card.client_name || card.client_identifier || 'Sin nombre' }}</span>
                <span v-if="card.intake_id" class="mx-card__hash" :title="`Intake #${card.intake_id}`">#{{ card.intake_id }}</span>
                <span
                  v-if="hiddenDuplicates(card)"
                  class="mx-card__dup"
                  :title="`Este lead tiene ${hiddenDuplicates(card) + 1} conversaciones abiertas en distintas secciones. Se muestra la de esta columna.`"
                >+{{ hiddenDuplicates(card) }}</span>
                <span
                  class="mx-card__wa"
                  :class="{ 'is-bad': !isCardPhoneComplete(card) }"
                  :title="isCardPhoneComplete(card) ? 'Número completo' : 'Número incompleto, no se puede escribir por WhatsApp'"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </span>
              </span>
              <span class="mx-card__tel">{{ cardPhone(card) || 'sin teléfono' }}</span>
            </span>

            <!-- El tono dice qué clase de dato es; la intensidad, qué tan mal está -->
            <span class="mx-card__pills">
              <span
                v-if="waitOf(card) > 0"
                class="mx-pil mx-pil--wait"
                :class="waitLevel(card)"
                :title="`El cliente escribió hace ${durOf(waitOf(card))} y todavía nadie contestó`"
              ><i>◷</i><b>{{ durOf(waitOf(card)) }}</b> sin responder</span>

              <span
                v-if="!isFinalStage(etapa, i)"
                class="mx-pil mx-pil--stage"
                :class="stageLevel(card)"
                :title="slaMessage(card, i) || 'Tiempo en esta etapa'"
              ><i>⧗</i><b>{{ stageElapsed(card) }}</b> en etapa</span>

              <span
                v-if="firstContactLabel(card)"
                class="mx-pil mx-pil--first"
                title="Tardanza del primer contacto: de la creación al primer mensaje saliente"
              ><i>☏</i>1er contacto <b>{{ firstContactLabel(card) }}</b></span>
              <span v-else class="mx-pil mx-pil--none" title="Todavía nadie le escribió"><i>☏</i>sin contactar</span>

              <span v-if="originBadge(card)" class="mx-pil mx-pil--src" :title="originBadge(card).title">
                <i class="mx-dot" :class="originDotOf(card)"></i>{{ originBadge(card).label }}
              </span>

              <span v-if="card.is_urgent" class="mx-pil mx-pil--wait is-risk" title="Marcada como urgente"><i>●</i>urgente</span>
            </span>

            <button
              v-if="showReasonBtn(card, etapa)"
              type="button"
              class="mx-card__why"
              :title="`Ver por qué esta conversación está en ${reasonStageLabel(card, etapa)}`"
              @click.stop="openReasonModal(card, etapa)"
            ><i>⚠</i>{{ reasonText(card) }}</button>
          </article>

          <div v-if="columns[etapa.key] && columns[etapa.key].loadingMore" class="mx-col__e">Cargando…</div>
          <div v-else-if="visibleCards(etapa).length === 0" class="mx-col__e">
            {{ isColFiltered(etapa.key) ? 'Sin resultados' : 'Sin conversaciones' }}
          </div>
        </div>
      </section>

      <!-- ── Sección principal: las otras secciones como columnas ── -->
      <template v-if="isPrincipalSection && renderedSectionColumns.length">
        <div class="mx-board__div" aria-hidden="true"></div>

        <section
          v-for="(col, j) in renderedSectionColumns"
          :key="'sec-' + col.id"
          class="mx-pane mx-col"
          :class="{ 'is-over': dragOverSectionId === col.id }"
          :style="hueVars({ key: 'sec' }, j + 3)"
          :data-section-id="col.id"
          @dragover.prevent="onSectionColDragOver(col)"
          @dragleave="onSectionColDragLeave(col)"
          @drop.prevent="onDropSectionCol(col)"
        >
          <header class="mx-col__h">
            <span class="mx-col__dot"></span>
            <span class="mx-col__id">
              <span class="mx-col__nm">{{ col.label }}</span>
              <span class="mx-col__sub">
                <template v-if="sectionFirstContactAvg(col)">1er contacto · visibles <b>{{ sectionFirstContactAvg(col) }}</b></template>
                <template v-else>otra sección</template>
              </span>
            </span>
            <span class="mx-col__n">
              {{ isColFiltered(sectionColKey(col)) ? visibleSectionCards(col).length : sectionColCountLabel(col) }}
            </span>
            <span class="mx-col__tools">
              <button type="button" class="mx-col__tool" :class="{ 'is-on': colSearch.key === sectionColKey(col) }" title="Buscar en esta columna" @click.stop="toggleColSearch(sectionColKey(col))">⌕</button>
            </span>
          </header>

          <div v-if="colSearch.key === sectionColKey(col)" class="mx-col__find">
            <input ref="colSearchInput" v-model="colSearch.query" type="text" placeholder="Nombre o teléfono…" @keydown.esc="closeColSearch" />
            <button type="button" class="mx-col__tool" title="Cerrar" @click="closeColSearch">✕</button>
          </div>

          <div class="mx-col__b" @scroll="onSectionColScroll(col, $event)">
            <article
              v-for="card in visibleSectionCards(col)"
              :key="card.id"
              class="mx-card"
              :class="{ 'is-drag': dragged && dragged.card.id === card.id }"
              :draggable="!selectMode"
              @click="onCardClick(card)"
              @dragstart="onSectionCardDragStart(card, col, $event)"
              @dragend="onDragEnd"
              @touchstart="onSectionCardTouchStart(card, col, $event)"
              @touchmove="onCardTouchMove($event)"
              @touchend="onCardTouchEnd($event)"
              @touchcancel="onCardTouchCancel"
            >
              <span class="mx-card__bar" :class="riskClass(card, null)"></span>
              <span class="mx-card__av">{{ initials(card.client_name || card.client_identifier) }}</span>
              <span class="mx-card__head">
                <span class="mx-card__l1">
                  <span class="mx-card__nm">{{ card.client_name || card.client_identifier || 'Sin nombre' }}</span>
                  <span v-if="card.intake_id" class="mx-card__hash">#{{ card.intake_id }}</span>
                  <span v-if="hiddenDuplicates(card)" class="mx-card__dup">+{{ hiddenDuplicates(card) }}</span>
                  <span class="mx-card__wa" :class="{ 'is-bad': !isCardPhoneComplete(card) }">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </span>
                </span>
                <span class="mx-card__tel">{{ cardPhone(card) || 'sin teléfono' }}</span>
              </span>
              <span class="mx-card__pills">
                <span v-if="waitOf(card) > 0" class="mx-pil mx-pil--wait" :class="waitLevel(card)"><i>◷</i><b>{{ durOf(waitOf(card)) }}</b> sin responder</span>
                <span class="mx-pil mx-pil--stage" :class="stageLevel(card)"><i>⧗</i><b>{{ stageElapsed(card) }}</b> en etapa</span>
                <span v-if="firstContactLabel(card)" class="mx-pil mx-pil--first"><i>☏</i>1er contacto <b>{{ firstContactLabel(card) }}</b></span>
                <span v-if="originBadge(card)" class="mx-pil mx-pil--src"><i class="mx-dot" :class="originDotOf(card)"></i>{{ originBadge(card).label }}</span>
              </span>
              <button v-if="showReasonBtn(card, null)" type="button" class="mx-card__why" @click.stop="openReasonModal(card, null)"><i>⚠</i>{{ reasonText(card) }}</button>
            </article>

            <div v-if="col.loadingMore" class="mx-col__e">Cargando…</div>
            <div v-else-if="visibleSectionCards(col).length === 0" class="mx-col__e">
              {{ isColFiltered(sectionColKey(col)) ? 'Sin resultados' : 'Sin conversaciones' }}
            </div>
          </div>
        </section>
      </template>
    </div>

    <!-- ══ MUELLE DE SELECCIÓN ══ -->
    <transition name="mx-dock">
      <div v-if="selectMode && selectedCardIds.length" class="mx-dock">
        <span class="mx-dock__n">{{ selectedCardIds.length }}</span>
        <span>{{ selectedCardIds.length === 1 ? 'conversación marcada' : 'conversaciones marcadas' }}</span>
        <span class="mx-sep"></span>
        <div class="mx-pop-wrap">
          <button type="button" class="mx-act is-on" :disabled="boardLoading" @click.stop="showMovePicker = !showMovePicker">Mover a sección ▾</button>
          <div v-if="showMovePicker" class="mx-pop mx-pop--sm" style="top:auto;bottom:calc(100% + 8px);left:50%;right:auto;transform:translateX(-50%)" @click.stop>
            <button v-for="sec in movableSections" :key="sec.status" type="button" class="mx-pop__item" @click="moveSelectedToSection(sec)">{{ sec.label }}</button>
            <div v-if="!movableSections.length" class="mx-col__e">No hay otras secciones</div>
          </div>
        </div>
        <span class="mx-sep"></span>
        <span class="mx-dock__hint" style="color:var(--mx-ink-3)">o arrastralas al espinazo</span>
        <button type="button" class="mx-dock__x" title="Limpiar la selección" @click="toggleSelectMode">✕</button>
      </div>
    </transition>

    <!-- ══ MODALES ══ -->
    <transition name="mx-fade">
      <div v-if="intakeModal.open" class="mx-ov" @click.self="cancelIntake">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g is-risk">⚠</span>
            <div>
              <h3>Mover a "{{ intakeModal.toLabel || 'Intake fallido' }}"</h3>
              <p>Esta columna pide un motivo. Queda registrado con tu nombre.</p>
            </div>
          </div>
          <div class="mx-modal__b">
            <label class="mx-fld">
              <span>Motivo</span>
              <textarea ref="razonInput" v-model="intakeModal.razon" class="mx-in" rows="3" maxlength="500" placeholder="Ej: el cliente no calificó, datos inválidos, no respondió…" @keydown.escape="cancelIntake"></textarea>
            </label>
            <p v-if="intakeModal.error" class="mx-err">{{ intakeModal.error }}</p>
          </div>
          <div class="mx-modal__f">
            <button type="button" class="mx-btn" @click="cancelIntake">Cancelar</button>
            <button type="button" class="mx-btn mx-btn--risk" @click="confirmIntake">Confirmar</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="mx-fade">
      <div v-if="reasonModal.open" class="mx-ov" @click.self="closeReasonModal">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g is-risk">⚠</span>
            <div><h3>{{ reasonModal.stage }}</h3><p>{{ reasonModal.name }}</p></div>
          </div>
          <div class="mx-modal__b">
            <p v-if="reasonModal.reason" class="mx-note">{{ reasonModal.reason }}</p>
            <p v-else class="mx-note" style="color:var(--mx-ink-3)">No hay un motivo registrado para esta conversación en esta columna.</p>
            <p v-if="reasonModal.by || reasonModal.at" class="mx-meta">
              <span v-if="reasonModal.by">Registrado por {{ reasonModal.by }}</span>
              <span v-if="reasonModal.by && reasonModal.at"> · </span>
              <span v-if="reasonModal.at">{{ formatReasonDate(reasonModal.at) }}</span>
            </p>
          </div>
          <div class="mx-modal__f"><button type="button" class="mx-btn" @click="closeReasonModal">Cerrar</button></div>
        </div>
      </div>
    </transition>

    <transition name="mx-fade">
      <div v-if="stageModal.open && stageModal.mode !== 'delete'" class="mx-ov" @click.self="closeStageModal">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g">{{ stageModal.mode === 'create' ? '＋' : '✎' }}</span>
            <div>
              <h3>{{ stageModal.mode === 'create' ? 'Nueva columna' : 'Renombrar columna' }}</h3>
              <p>{{ stageModal.mode === 'create' ? 'Elegí el nombre y en qué secciones aparece.' : 'El cambio se aplica en las secciones que marques.' }}</p>
            </div>
          </div>
          <div class="mx-modal__b">
            <label class="mx-fld">
              <span>Nombre</span>
              <input ref="stageNameInput" v-model="stageModal.nombre" class="mx-in" type="text" maxlength="100" placeholder="Ej: Esperando documentación" @keydown.escape="closeStageModal" @keydown.enter="saveStage" />
            </label>
            <div style="display:flex;gap:16px;align-items:center">
              <label class="mx-fld" style="flex:0 0 auto">
                <span>Color</span>
                <input v-model="stageModal.color" type="color" class="mx-color" />
              </label>
              <label class="mx-ck" style="margin-top:14px">
                <input v-model="stageModal.pedirRazon" type="checkbox" />
                <span>Pedir un motivo al mover un chat acá</span>
              </label>
            </div>
            <div class="mx-fld">
              <span>{{ stageModal.mode === 'create' ? 'Aparece en estas secciones' : 'Aplicar el cambio en estas secciones' }}</span>
              <div v-if="stageModal.mode === 'edit' && stageModal.loadingSections" class="mx-col__e">Cargando secciones…</div>
              <div v-else style="display:flex;flex-direction:column;gap:5px;max-height:150px;overflow:auto">
                <label v-for="sec in sectionChoices" :key="sec.id" class="mx-ck">
                  <input type="checkbox" :checked="stageModal.sectionIds.includes(sec.id)" @change="toggleStageSection(sec.id)" />
                  <span>{{ sec.label }}</span>
                </label>
                <div v-if="!sectionChoices.length" class="mx-col__e">No hay secciones disponibles</div>
              </div>
            </div>
            <p v-if="stageModal.error" class="mx-err">{{ stageModal.error }}</p>
          </div>
          <div class="mx-modal__f">
            <button type="button" class="mx-btn" :disabled="stageModal.saving" @click="closeStageModal">Cancelar</button>
            <button type="button" class="mx-btn mx-btn--go" :disabled="stageModal.saving" @click="saveStage">
              {{ stageModal.saving ? 'Guardando…' : (stageModal.mode === 'create' ? 'Crear columna' : 'Guardar') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="mx-fade">
      <div v-if="stageModal.open && stageModal.mode === 'delete'" class="mx-ov" @click.self="closeStageModal">
        <div class="mx-modal">
          <div class="mx-modal__h">
            <span class="mx-modal__g is-risk">🗑</span>
            <div>
              <h3>Eliminar "{{ stageModal.nombre }}"</h3>
              <p>Se elimina en todas las secciones que tienen esta columna. Si le quedan chats, no se puede borrar.</p>
            </div>
          </div>
          <div class="mx-modal__b"><p v-if="stageModal.error" class="mx-err">{{ stageModal.error }}</p></div>
          <div class="mx-modal__f">
            <button type="button" class="mx-btn" :disabled="stageModal.saving" @click="closeStageModal">Cancelar</button>
            <button type="button" class="mx-btn mx-btn--risk" :disabled="stageModal.saving" @click="saveStage">
              {{ stageModal.saving ? 'Eliminando…' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="mx-toast">
      <div v-if="toast.open" class="mx-toast" :class="{ 'is-bad': toast.type === 'error' }">
        <span><b>{{ toast.type === 'error' ? 'No se pudo' : 'Listo' }}</b>{{ toast.message }}</span>
      </div>
    </transition>

    <!-- Fantasma del arrastre táctil -->
    <div v-if="touchDrag.active && touchDrag.card" class="mx-ghost" :style="{ left: touchDrag.x + 'px', top: touchDrag.y + 'px' }">
      <span class="mx-ghost__av">{{ initials(touchDrag.card.client_name || touchDrag.card.client_identifier) }}</span>
      <span>{{ touchDrag.card.client_name || touchDrag.card.client_identifier || 'Sin nombre' }}</span>
    </div>
    <template v-if="touchDrag.active">
      <div class="mx-edge mx-edge--l" :class="{ 'is-on': autoScrollHDir === -1 }" aria-hidden="true">‹</div>
      <div class="mx-edge mx-edge--r" :class="{ 'is-on': autoScrollHDir === 1 }" aria-hidden="true">›</div>
    </template>
  </div>
</template>
<script>
import apiUrls from '@/new-constants/apiUrls'
import { dur, waitingSecs, stageSecs, riskOf, originDot, stageHue } from './ui'


const ETAPAS = [
  { key: 'sin_asignar',         label: 'Sin asignar',         icon: '📥' },
  { key: 'asignado',            label: 'Asignado',            icon: '🙋' },
  { key: 'contactado',          label: 'Contactado',          icon: '📞' },
  { key: 'en_conversacion',     label: 'En conversación',     icon: '💬' },
  { key: 'esperando_respuesta', label: 'Esperando respuesta', icon: '⏳' },
  { key: 'visita_agendada',     label: 'Visita agendada',     icon: '📅' },
  { key: 'pago_pendiente',      label: 'Pago pendiente',      icon: '💳' },
  { key: 'alquiler_exitoso',    label: 'Alquiler exitoso',    icon: '✅' },
  { key: 'intake_fallido',      label: 'Intake fallido',      icon: '❌' },
  { key: 'en_espera',           label: 'En espera',           icon: '🕓' },
]

const STATUS_LABELS = {
  active: 'Activa', pending: 'Pendiente', escalated: 'Escalada',
  reopened: 'Reabierta', cold: 'Fría', closed: 'Cerrada', erasmusplay: 'ErasmusPlay'
}

export default {
  name: 'BoardV2',
  props: {
    // Sección (status) a la que pertenece este tablero. Si es null/'' muestra todo.
    status: { type: String, default: null },
    // Instancia (evolution_instance) que actúa como sección. Opcional.
    instance: { type: String, default: null },
    // Etiqueta a mostrar en el encabezado del tablero.
    label: { type: String, default: 'Tablero de conversaciones' },
    // Secciones disponibles para "Mover a sección".
    sections: { type: Array, default: () => [] },
    // ¿El usuario puede administrar columnas (crear/renombrar/eliminar)?
    // Solo los admins con acceso total (isSuperAdmin del dashboard).
    canManageStages: { type: Boolean, default: false },
    // ¿El usuario es super admin (hardcodeado en el dashboard)? Se usa para que
    // solo él pueda devolver tarjetas HACIA la columna "Asignación". Sacarlas de
    // ahí lo puede hacer cualquiera.
    isSuperAdmin: { type: Boolean, default: false },
    // La búsqueda global y el filtro de origen los maneja la barra superior
    // del dashboard; acá entran como props y se copian al estado interno.
    query: { type: String, default: '' },
    origin: { type: String, default: '' },
    // Rango de fechas del último movimiento de sección ('YYYY-MM-DD', las dos
    // inclusive, vacío = sin filtro). Lo elige la barra superior; acá se copia
    // al estado interno y de ahí sale a la API como ?desde / ?hasta.
    desde: { type: String, default: '' },
    hasta: { type: String, default: '' },
  },
  computed: {
    filterKey() { return `${this.status || ''}|${this.instance || ''}` },
    // Las dos puntas del rango en una sola clave: cambian juntas y así el
    // tablero se recarga una vez y no dos.
    propDates() { return `${this.desde || ''}|${this.hasta || ''}` },
    // Secciones a las que se puede mover (todas menos la actual).
    movableSections() {
      return (this.sections || []).filter(s => s && s.status !== this.status)
    },
    // Sección actualmente visible (según su slug).
    currentSection() {
      return (this.sections || []).find(s => s && s.status === this.status) || null
    },
    // ¿La sección actual es la principal? (seccion_principal = 1)
    isPrincipalSection() {
      return !!(this.currentSection && this.currentSection.seccion_principal)
    },
    // Columnas-sección a renderizar: las que devolvió el backend, acotadas a
    // las secciones que el usuario puede ver (movableSections).
    renderedSectionColumns() {
      if (!this.isPrincipalSection || !this.sectionColumns.length) return []
      const allowed = new Set(this.movableSections.map(s => String(s.id)))
      return this.sectionColumns.filter(sc => allowed.has(String(sc.id)))
    },
    // Secciones que se pueden elegir al crear una columna (las que tienen id).
    selectableSections() {
      return (this.sections || []).filter(s => s && s.id != null)
    },
    // Secciones a listar en el modal: al crear, todas las disponibles; al
    // editar, solo las que ya tienen esta columna.
    sectionChoices() {
      if (this.stageModal.mode === 'edit') return this.stageModal.sectionOptions || []
      return this.selectableSections
    },
    // Todas las tarjetas cargadas (etapas + columnas-sección).
    allLoadedCards() {
      const out = []
      Object.keys(this.columns).forEach(k => {
        const col = this.columns[k]
        if (col && col.cards) out.push(...col.cards)
      })
      this.sectionColumns.forEach(col => { if (col && col.cards) out.push(...col.cards) })
      return out
    },
    // Índice de leads repetidos: clave del lead → { winner, count }.
    //
    // Solo se arma con la búsqueda GLOBAL activa, que es cuando se busca a UNA
    // persona y verla clonada en cada columna es puro ruido. Queda fuera a
    // propósito la búsqueda por columna: ahí el ganador podría vivir en otra
    // columna y la búsqueda no devolvería nada. Y sin búsqueda el tablero
    // muestra todo tal cual, para no desalinear los contadores de las columnas.
    dedupeIndex() {
      const index = new Map()
      const globalQuery = String(this.globalSearch).trim()
      if (!globalQuery) return index

      this.allLoadedCards.forEach(card => {
        if (!this.matchesTag(card)) return
        if (!this.matchesQuery(card, globalQuery)) return
        const key = this.dedupeKey(card)
        if (!key) return

        const prev = index.get(key)
        if (!prev) { index.set(key, { winner: card, count: 1 }); return }
        prev.count++
        if (this.dedupeBeats(card, prev.winner)) prev.winner = card
      })
      return index
    },
    // Cuántas conversaciones del tablero casan con la búsqueda global, ya
    // deduplicadas: el número tiene que coincidir con las tarjetas en pantalla.
    globalMatchesCount() {
      if (!String(this.globalSearch).trim()) return 0
      const index = this.dedupeIndex
      return this.allLoadedCards.filter(c => {
        if (!this.matchesTag(c) || !this.matchesQuery(c, this.globalSearch)) return false
        const entry = index.get(this.dedupeKey(c))
        return !entry || entry.count <= 1 || entry.winner === c
      }).length
    },
    // ─── Filtro de fechas (last_move_seccion) ───────────────────
    hasDateFilter() {
      return !!(this.dateFilter.desde || this.dateFilter.hasta)
    },
    // Texto del botón: "Fechas" sin filtro, o el rango aplicado en dd/mm.
    dateFilterLabel() {
      const { desde, hasta } = this.dateFilter
      if (desde && hasta) {
        return desde === hasta ? this.shortDate(desde) : `${this.shortDate(desde)} – ${this.shortDate(hasta)}`
      }
      if (desde) return `Desde ${this.shortDate(desde)}`
      if (hasta) return `Hasta ${this.shortDate(hasta)}`
      return 'Fechas'
    },
    datePresets() {
      return [
        { key: 'hoy',    label: 'Hoy' },
        { key: 'ayer',   label: 'Ayer' },
        { key: 'semana', label: 'Últimos 7 días' },
        { key: 'mes',    label: 'Este mes' },
      ]
    },
    // Atajo que coincide con lo que hay cargado en el menú (para pintarlo).
    activeDatePreset() {
      const found = this.datePresets.find(p => {
        const r = this.presetRange(p.key)
        return r.desde === this.draftDates.desde && r.hasta === this.draftDates.hasta
      })
      return found ? found.key : null
    },
    // Códigos de campaña (codigo_ads) presentes en el tablero, para acotar el
    // filtro de Meta a una campaña concreta.
    adsCodeOptions() {
      const set = new Set()
      this.allLoadedCards.forEach(c => {
        if (c && c.lead_origin === 'meta' && c.lead_ads_code) set.add(String(c.lead_ads_code))
      })
      return Array.from(set).sort()
    },
  },
  watch: {
    query: {
      immediate: true,
      handler (v) { if (this.globalSearch !== v) this.globalSearch = v || '' }
    },
    origin: {
      immediate: true,
      handler (v) { if (this.tagFilter !== (v || null)) this.tagFilter = v || null }
    },
    // Cambio de sección: el tablero es otro (otras columnas), así que va la
    // carga completa. Los filtros de la barra son globales y no se limpian,
    // pero los del front solo ven lo que el scroll trajo: si hay alguno activo
    // hay que traer todas las páginas para que filtre sobre el total.
    filterKey() {
      this.resetBoardFilters()
      this.fetchBoard().then(() => {
        if (String(this.globalSearch).trim() || this.tagFilter) this.loadAllColumns()
      })
    },
    // El rango de fechas lo aplica el BACKEND, así que cambiarlo es volver a
    // pedir el tablero: los totales de cada columna y el scroll infinito
    // tienen que llegar ya filtrados. Va con el armazón puesto: las columnas
    // son las mismas, solo cambia qué cae adentro. La carga inicial no pasa por
    // acá —`dateFilter` nace de las props en data()—, de eso se encarga created().
    propDates() {
      this.dateFilter = { desde: this.desde || '', hasta: this.hasta || '' }
      this.draftDates = { ...this.dateFilter }
      this.fetchBoard({ keepFrame: true })
    },
    // Igual que con el tag: para buscar en TODAS las columnas hay que traer el
    // tablero completo, porque el filtro es del lado del front y solo ve lo que
    // el scroll fue cargando. Se espera un momento para no disparar en cada tecla.
    globalSearch(val) {
      clearTimeout(this._globalSearchTimer)
      if (!String(val).trim()) return
      this._globalSearchTimer = setTimeout(() => {
        this.globalSearchLoading = true
        Promise.resolve(this.loadAllColumns()).finally(() => { this.globalSearchLoading = false })
      }, 350)
    },
    // Al activar un tag hay que traer TODO el tablero: el filtro es del lado del
    // front y solo ve las tarjetas ya cargadas.
    tagFilter(val) {
      if (val !== 'meta') this.adsCodeFilter = ''
      if (val) this.loadAllColumns()
    },
  },
  data() {
    return {
      etapas: ETAPAS,
      // Búsqueda global del tablero (al lado del nombre de la sección): filtra
      // TODAS las columnas por nombre, apellido o teléfono.
      globalSearch: '',
      globalSearchLoading: false,
      // Búsqueda por columna: { key: columna activa, query }. La key es el slug
      // de la etapa, o 'sec:<id>' si es una columna-sección.
      colSearch: { key: null, query: '' },
      // Filtro por tag/origen del lead: null | 'erasmusplay' | 'meta' | 'directo'.
      tagFilter: null,
      // Al filtrar por Meta se puede acotar a un codigo_ads concreto ('' = todos).
      adsCodeFilter: '',
      // Filtro por rango de fechas (last_move_seccion). `dateFilter` es el rango
      // APLICADO —el que viaja al backend como ?desde/?hasta—; `draftDates` es
      // lo que se está eligiendo en el menú hasta apretar "Aplicar".
      //
      // En la v2 el menú vive en la barra superior (DashboardV2), así que los
      // dos arrancan de las props: el tablero puede montarse con un rango ya
      // elegido y la primera carga tiene que salir filtrada.
      dateFilter: { desde: this.desde || '', hasta: this.hasta || '' },
      draftDates: { desde: this.desde || '', hasta: this.hasta || '' },
      dateMenuOpen: false,
      // true mientras se traen todas las páginas del tablero para que el filtro
      // por tag aplique sobre el total y no solo sobre lo cargado por scroll.
      tagLoading: false,
      // Generación del tablero: se incrementa en cada carga (y al cambiar de
      // sección). Las respuestas de páginas pedidas para el tablero anterior
      // llegan con una generación vieja y se descartan, para que no se mezclen
      // con las tarjetas de la sección que se está viendo.
      boardSeq: 0,
      tagOptions: [
        { value: null,          label: 'Todos',       cls: '',                            title: 'Sin filtro de origen' },
        { value: 'erasmusplay', label: '🎓 ErasmusPlay', cls: 'kanban__tag-pill--erasmus', title: 'Leads de ErasmusPlay' },
        { value: 'meta',        label: '📣 Meta',        cls: 'kanban__tag-pill--meta',    title: 'Leads de campañas de Meta (con codigo_ads)' },
        { value: 'directo',     label: '🌐 Directo',     cls: 'kanban__tag-pill--directo', title: 'Leads directos de la web' },
      ],
      // Id de la tarjeta que muestra el gif de festejando (1 sola vez al mover de columna)
      celebrateCardId: null,
      isSectionMode: false,
      selectMode: false,
      selectedCardIds: [],
      showMovePicker: false,
      columns: {},
      // Columnas-sección (otras secciones) que muestra la sección principal.
      sectionColumns: [],
      boardLoading: true,
      boardError: null,
      limit: 20,
      dragged: null,           // { card, fromEtapa }
      dragOverEtapa: null,
      dragOverSectionId: null,
      // Arrastre táctil (celular/tablet): long-press para levantar la tarjeta.
      touchDrag: { active: false, card: null, fromEtapa: null, fromSectionId: null, x: 0, y: 0, startX: 0, startY: 0 },
      // Sentido del auto-scroll horizontal en curso (-1 izq, 0 nada, 1 der).
      // Solo para pintar la franja del borde durante el arrastre táctil.
      autoScrollHDir: 0,
      intakeModal: { open: false, card: null, fromEtapa: null, toEtapa: null, razon: '', error: '' },
      // Pop-up con el motivo por el que una conversación está en su columna
      // (las columnas creadas con "pedir razón" al mover).
      reasonModal: { open: false, name: '', stage: '', reason: '', by: '', at: '' },
      toast: { open: false, type: 'error', message: '', timer: null },
      // Reloj que se refresca solo para el "tiempo en etapa" de cada tarjeta.
      nowTick: Date.now(),
      // Modal de gestión de columnas (crear/renombrar/eliminar). mode: 'create'|'edit'|'delete'.
      stageModal: {
        open: false,
        mode: null,
        slug: null,
        nombre: '',
        color: '#B2BEC3',
        pedirRazon: false,
        sectionIds: [],
        // Al editar: secciones que YA tienen esta columna (para elegir a cuáles
        // aplicar el cambio) y estado de carga de esa lista.
        sectionOptions: [],
        loadingSections: false,
        error: '',
        saving: false,
      },
    }
  },
  created() {
    this.initColumns()
    this.fetchBoard()
    // Refresca el tiempo transcurrido de las tarjetas cada 30s.
    this._nowTimer = setInterval(() => { this.nowTick = Date.now() }, 30000)
  },
  mounted() {
    document.addEventListener('click', this.onDocClickDates)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClickDates)
    if (this._autoScrollRAF) cancelAnimationFrame(this._autoScrollRAF)
    document.removeEventListener('dragover', this.onDocDragOver)
    clearTimeout(this._touchTimer)
    clearTimeout(this._globalSearchTimer)
    clearInterval(this._nowTimer)
    if (this._celebrateTimer) clearTimeout(this._celebrateTimer)
  },
  methods: {

    // ── Presentación de la v2 ──────────────────────────────────
    // El tono de cada etapa: identifica la columna sin leer el encabezado.
    // Si la etapa trae color propio del backend, ese manda.
    hueVars (etapa, i) {
      const h = etapa && etapa.color ? etapa.color : stageHue(etapa, i).h
      const pal = stageHue(etapa, i)
      return {
        '--h': h,
        // Mismas intensidades que la paleta de ui.js, para que una columna con
        // color propio del backend no se vea más pálida que las de al lado.
        '--h-soft': etapa && etapa.color ? this.tint(h, 0.09) : pal.soft,
        '--h-line': etapa && etapa.color ? this.tint(h, 0.22) : pal.line
      }
    },
    /**
     * Hex → rgba con la transparencia que pida el llamador.
     *
     * El \d de la clase NO es opcional: sin él la clase queda [a-f] + la letra
     * "d", y entonces cualquier color con un dígito adentro —o sea, casi todos:
     * #2C6FD6, #B4791A— no matchea y se cae al gris de respaldo. El color propio
     * de la columna se veía solo en el punto y el título, y el lavado de las
     * tarjetas quedaba igual en todas las columnas.
     */
    tint (hex, a) {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''))
      if (!m) return 'rgba(91,107,135,' + a + ')'
      return 'rgba(' + parseInt(m[1], 16) + ',' + parseInt(m[2], 16) + ',' + parseInt(m[3], 16) + ',' + a + ')'
    },

    durOf (secs) { return dur(secs) },
    waitOf (card) { const w = waitingSecs(card); return w == null ? 0 : w },
    isFinalStage (etapa, i) { return !this.isAlertColumn(i) },

    /** El canto de la tarjeta: el único dato que necesita color. */
    riskClass (card, i) {
      return riskOf(card, i != null && !this.isAlertColumn(i))
    },
    columnHasRisk (etapa, i) {
      return this.visibleCards(etapa).some(c => this.riskClass(c, i) === 'risk')
    },
    /** Dentro de cada familia, la intensidad dice qué tan mal está. */
    waitLevel (card) {
      const w = this.waitOf(card)
      return w >= 86400 ? 'is-risk' : w >= 14400 ? 'is-warn' : ''
    },
    stageLevel (card) {
      const s = stageSecs(card) || 0
      return s >= 259200 ? 'is-risk' : s >= 172800 ? 'is-warn' : ''
    },
    originDotOf (card) { return originDot(card) },
    reasonText (card) {
      const r = this.humanReason(card)
      if (!r) return 'Ver por qué está acá'
      return r.length > 54 ? r.slice(0, 53).trim() + '…' : r
    },
    isCardDragging (card) {
      if (this.dragged && this.dragged.card.id === card.id) return true
      return !!(this.touchDrag.active && this.touchDrag.card && this.touchDrag.card.id === card.id)
    },

    /**
     * Soltar en una sección del espinazo. Lo llama el dashboard, que es quien
     * tiene las placas; acá vive la tarjeta que se está arrastrando.
     */
    dropOnSection (section) {
      const card = (this.dragged && this.dragged.card) || (this.touchDrag && this.touchDrag.card)
      if (!card || !section || !section.id) return false
      // Igual que el picker del muelle: el que llama a la API es el dashboard.
      this.$emit('move-to-section', { ids: [card.id], sectionId: section.id })
      this.dragged = null
      return true
    },
    initColumns() {
      const cols = {}
      ETAPAS.forEach(e => {
        cols[e.key] = { cards: [], nextCursor: null, loadingMore: false }
      })
      this.columns = cols
    },
    // Deja las columnas que ya están en pantalla vacías y en "Cargando…", sin
    // tocar ni las etapas ni las columnas-sección: el tablero sigue ahí, lo que
    // se recarga es el contenido. Es lo que sostiene el modo keepFrame.
    markColumnsLoading() {
      Object.keys(this.columns).forEach(k => {
        const col = this.columns[k]
        if (col) { col.cards = []; col.nextCursor = null; col.loadingMore = true }
      })
      this.sectionColumns.forEach(col => {
        if (col) { col.cards = []; col.nextCursor = null; col.loadingMore = true }
      })
    },
    clearColumnsLoading() {
      Object.keys(this.columns).forEach(k => { if (this.columns[k]) this.columns[k].loadingMore = false })
      this.sectionColumns.forEach(col => { if (col) col.loadingMore = false })
    },
    token() { return localStorage.getItem('token') || '' },

    // Ícono para las etapas de sección (conversation_stage no trae ícono).
    stageIcon(stage) {
      return (stage && stage.es_final) ? '🏁' : '🗂️'
    },

    /**
     * Trae el tablero. Tres modos, según qué se pueda dar por sabido:
     *
     * · normal      — primera carga o cambio de sección: no se sabe ni qué
     *                 columnas hay, así que se muestra "Cargando el tablero…".
     * · keepFrame   — cambió un filtro: las columnas son las mismas y solo
     *                 cambia qué cae adentro. Se vacían las tarjetas y cada
     *                 columna muestra su propio "Cargando…", pero el armazón
     *                 no se va. Sin esto, filtrar hacía desaparecer el tablero
     *                 entero por un segundo y volvía: se perdía el lugar.
     * · silent      — tras mover una tarjeta: ni spinner ni vaciado, para
     *                 refrescar en vivo sin flash.
     */
    async fetchBoard({ silent = false, keepFrame = false } = {}) {
      // Nueva generación: invalida las páginas que quedaron en vuelo del tablero
      // anterior (búsqueda global o por columna incluidas).
      const seq = ++this.boardSeq
      if (keepFrame && !silent) {
        this.boardError = null
        this.markColumnsLoading()
      } else if (!silent) {
        this.boardLoading = true
        this.boardError = null
        this.initColumns()
      }
      try {
        const params = new URLSearchParams({ limit: this.limit })
        // El backend resuelve ?status=<slug> como sección (conversation_section)
        // cuando no es un status del enum.
        if (this.status) params.set('status', this.status)
        if (this.instance) params.set('instance', this.instance)
        this.setDateParams(params)
        const res = await fetch(`${apiUrls.conversationsSupport}/board?${params}`, {
          headers: { Accept: 'application/json', Authorization: this.token() }
        })
        if (!res.ok) throw new Error('board request failed')
        const json = await res.json()
        // Llegó tarde: ya se pidió otro tablero (otra sección), se descarta.
        if (seq !== this.boardSeq) return
        const data = json && json.data ? json.data : {}

        if (Array.isArray(json.stages) && json.stages.length) {
          // Modo SECCIÓN: columnas = etapas de la sección (conversation_stage).
          this.isSectionMode = true
          this.etapas = json.stages.map(s => ({
            key: s.slug,
            label: s.label || s.nombre,
            icon: this.stageIcon(s),
            color: s.color || null,
            stage_id: s.id,
            es_final: !!s.es_final,
            pedir_razon_fallo: !!s.pedir_razon_fallo,
          }))
          const cols = {}
          this.etapas.forEach(e => {
            const col = data[e.key] || { data: [], next_cursor: null }
            cols[e.key] = {
              cards: col.data || [],
              nextCursor: col.next_cursor || null,
              loadingMore: false,
              stage_id: (col.stage_id != null) ? col.stage_id : e.stage_id,
              // Total REAL de chats en la etapa (no el total cargado del listado).
              total: (typeof col.total === 'number') ? col.total : (col.data || []).length,
            }
          })
          this.columns = cols
          this.buildSectionColumns(json.section_columns)
        } else {
          // Modo LEGACY: etapas del enum.
          this.isSectionMode = false
          this.etapas = ETAPAS
          const cols = {}
          ETAPAS.forEach(e => {
            const col = data[e.key] || { data: [], next_cursor: null }
            cols[e.key] = { cards: col.data || [], nextCursor: col.next_cursor || null, loadingMore: false }
          })
          this.columns = cols
          this.sectionColumns = []
        }
      } catch (err) {
        if (seq !== this.boardSeq) return
        // En modo silencioso no rompemos la vista: el movimiento ya se aplicó de
        // forma optimista; solo registramos el error del refresco en segundo plano.
        if (!silent) {
          // Si veníamos con el armazón puesto, las columnas quedaron marcadas
          // como cargando: hay que apagarlo o quedan girando abajo del error.
          this.clearColumnsLoading()
          this.boardError = 'No se pudo cargar el tablero.'
          this.sectionColumns = []
        } else {
          console.warn('[BoardV2] refresco silencioso falló:', err)
        }
      } finally {
        if (seq === this.boardSeq) this.boardLoading = false
      }
    },

    // Arma las columnas-sección (otras secciones) a partir de la respuesta.
    // Cada una recibe un color distinto (el de la sección o uno de la paleta).
    buildSectionColumns(raw) {
      if (!Array.isArray(raw)) { this.sectionColumns = []; return }
      const n = Math.max(raw.length, 1)
      this.sectionColumns = raw.map((sc, i) => {
        // Colores distintos y SIN repetición: matices repartidos en el círculo.
        const hue = Math.round((360 / n) * i)
        return {
          id: sc.id,
          slug: sc.slug,
          label: sc.label || sc.nombre,
          columnColor: `hsl(${hue}, 68%, 45%)`,   // acento (encabezado, punto)
          cardBg: `hsl(${hue}, 72%, 90%)`,         // fondo de color de las tarjetas
          cardBorder: `hsl(${hue}, 55%, 78%)`,     // borde de las tarjetas
          cards: sc.data || [],
          nextCursor: sc.next_cursor || null,
          loadingMore: false,
          // Total REAL de chats en la sección (no el total cargado del listado).
          total: (typeof sc.total === 'number') ? sc.total : (sc.data || []).length,
        }
      })
    },

    async fetchMoreSection(col) {
      if (!col || !col.nextCursor || col.loadingMore) return
      const seq = this.boardSeq
      col.loadingMore = true
      try {
        const params = new URLSearchParams({ limit: this.limit, cursor: col.nextCursor })
        if (this.status) params.set('status', this.status)
        params.set('section_col', col.id)
        if (this.instance) params.set('instance', this.instance)
        this.setDateParams(params)
        const res = await fetch(`${apiUrls.conversationsSupport}/board?${params}`, {
          headers: { Accept: 'application/json', Authorization: this.token() }
        })
        if (!res.ok) throw new Error('section column page failed')
        const json = await res.json()
        // El tablero cambió mientras se pedía la página: no mezclar tarjetas.
        if (seq !== this.boardSeq) return
        const incoming = json && json.data ? json.data : []
        const seen = new Set(col.cards.map(c => c.id))
        incoming.forEach(c => { if (!seen.has(c.id)) col.cards.push(c) })
        col.nextCursor = json && json.next_cursor ? json.next_cursor : null
      } catch (err) {
        if (seq === this.boardSeq) this.showToast('No se pudieron cargar más conversaciones.', 'error')
      } finally {
        col.loadingMore = false
      }
    },
    onSectionColScroll(col, evt) {
      const el = evt.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.fetchMoreSection(col)
      }
    },

    async fetchMore(etapaKey) {
      const col = this.columns[etapaKey]
      if (!col || !col.nextCursor || col.loadingMore) return
      const seq = this.boardSeq
      col.loadingMore = true
      try {
        const params = new URLSearchParams({ limit: this.limit, cursor: col.nextCursor })
        if (this.isSectionMode) {
          // En modo sección la columna es una etapa de la sección (slug).
          if (this.status) params.set('status', this.status)
          params.set('stage', etapaKey)
        } else {
          params.set('etapa', etapaKey)
        }
        if (this.instance) params.set('instance', this.instance)
        this.setDateParams(params)
        const res = await fetch(`${apiUrls.conversationsSupport}/board?${params}`, {
          headers: { Accept: 'application/json', Authorization: this.token() }
        })
        if (!res.ok) throw new Error('column page failed')
        const json = await res.json()
        // El tablero cambió mientras se pedía la página: no mezclar tarjetas.
        if (seq !== this.boardSeq) return
        const incoming = json && json.data ? json.data : []
        const seen = new Set(col.cards.map(c => c.id))
        incoming.forEach(c => { if (!seen.has(c.id)) col.cards.push(c) })
        col.nextCursor = json && json.next_cursor ? json.next_cursor : null
      } catch (err) {
        if (seq === this.boardSeq) this.showToast('No se pudieron cargar más conversaciones.', 'error')
      } finally {
        col.loadingMore = false
      }
    },

    onColScroll(etapaKey, evt) {
      const el = evt.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.fetchMore(etapaKey)
      }
    },

    // ─── Drag & drop ────────────────────────────────────────────
    onDragStart(card, fromEtapa, evt) {
      this.dragged = { card, fromEtapa, fromSectionId: null }
      if (evt.dataTransfer) {
        evt.dataTransfer.effectAllowed = 'move'
        try { evt.dataTransfer.setData('text/plain', card.id) } catch (e) { /* IE */ }
      }
      this.setCompactDragImage(card, evt)
      this.beginNativeDrag(evt)
    },
    // Arrastre iniciado desde una columna-sección (origen = sección).
    onSectionCardDragStart(card, col, evt) {
      this.dragged = { card, fromEtapa: null, fromSectionId: col ? col.id : null }
      if (evt.dataTransfer) {
        evt.dataTransfer.effectAllowed = 'move'
        try { evt.dataTransfer.setData('text/plain', card.id) } catch (e) { /* IE */ }
      }
      this.setCompactDragImage(card, evt)
      this.beginNativeDrag(evt)
    },
    // Reemplaza el "fantasma" de arrastre (por defecto es una captura de toda
    // la tarjeta, muy grande) por un chip compacto que solo muestra el nombre.
    setCompactDragImage(card, evt) {
      if (!evt || !evt.dataTransfer || typeof evt.dataTransfer.setDragImage !== 'function') return
      const name = card.client_name || card.client_identifier || 'Sin nombre'
      const ghost = document.createElement('div')
      ghost.textContent = name
      // El nodo se agrega al body, fuera del scope del componente, así que los
      // estilos van inline. Se posiciona fuera de pantalla hasta el snapshot.
      ghost.style.cssText = [
        'position:fixed', 'top:-1000px', 'left:-1000px', 'pointer-events:none',
        'padding:6px 12px', 'border-radius:8px', 'background:#FC7010', 'color:#fff',
        'font-size:13px', 'font-weight:700', 'font-family:inherit',
        'box-shadow:0 6px 16px rgba(0,0,0,.25)', 'white-space:nowrap', 'max-width:220px',
        'overflow:hidden', 'text-overflow:ellipsis', 'z-index:99999'
      ].join(';')
      document.body.appendChild(ghost)
      this._dragGhost = ghost
      try { evt.dataTransfer.setDragImage(ghost, 14, 18) } catch (e) { /* no soportado */ }
      // El navegador toma el snapshot de forma síncrona; el nodo ya se puede quitar.
      setTimeout(() => {
        if (this._dragGhost) { this._dragGhost.remove(); this._dragGhost = null }
      }, 0)
    },
    // El drag nativo (mouse) no emite mousemove, pero sí 'dragover' de forma
    // continua. Escuchamos a nivel documento para poder auto-scrollear también
    // en los huecos/bordes del tablero (no solo sobre una columna).
    beginNativeDrag(evt) {
      this._ptr = evt ? { x: evt.clientX, y: evt.clientY } : { x: 0, y: 0 }
      document.addEventListener('dragover', this.onDocDragOver)
    },
    onDocDragOver(evt) {
      if (!this.dragged) return
      // Necesario para que el puntero cuente como zona "soltable" y el auto-scroll
      // funcione en toda el área del tablero.
      evt.preventDefault()
      this._ptr = { x: evt.clientX, y: evt.clientY }
      this.computeAutoScroll(evt.clientX, evt.clientY)
    },
    endNativeDrag() {
      document.removeEventListener('dragover', this.onDocDragOver)
      this.stopAutoScroll()
      this._ptr = null
    },
    onDragOver(etapaKey) {
      if (!this.dragged) return
      this.dragOverEtapa = etapaKey
    },
    onDragLeave(etapaKey) {
      if (this.dragOverEtapa === etapaKey) this.dragOverEtapa = null
    },
    onDragEnd() {
      this.dragged = null
      this.dragOverEtapa = null
      this.dragOverSectionId = null
      if (this._dragGhost) { this._dragGhost.remove(); this._dragGhost = null }
      this.endNativeDrag()
    },

    // ─── Drop sobre una columna-sección (mover a otra sección) ──
    onSectionColDragOver(col) {
      if (!this.dragged || !col) return
      this.dragOverSectionId = col.id
      this.dragOverEtapa = null
    },
    onSectionColDragLeave(col) {
      if (col && this.dragOverSectionId === col.id) this.dragOverSectionId = null
    },
    onDropSectionCol(col) {
      const drag = this.dragged
      this.dragOverSectionId = null
      this.dragOverEtapa = null
      this.dragged = null
      if (!drag || !col || !col.id) return
      if (drag.fromSectionId === col.id) return   // misma sección: nada que hacer
      // Optimista: sacamos la tarjeta de su origen (etapa o sección); el padre
      // ejecuta el move y refresca el tablero (fetchBoard).
      this.removeDraggedFromOrigin(drag)
      this.$emit('move-to-section', { ids: [drag.card.id], sectionId: col.id })
    },
    // Quita la tarjeta arrastrada de su columna de origen (etapa o sección).
    removeDraggedFromOrigin(drag) {
      if (!drag) return
      if (drag.fromEtapa != null) {
        const from = this.columns[drag.fromEtapa]
        if (from) {
          const i = from.cards.findIndex(c => c.id === drag.card.id)
          if (i !== -1) {
            from.cards.splice(i, 1)
            if (typeof from.total === 'number') from.total = Math.max(0, from.total - 1)
          }
        }
      } else if (drag.fromSectionId != null) {
        const from = this.sectionColumns.find(c => c.id === drag.fromSectionId)
        if (from) {
          const i = from.cards.findIndex(c => c.id === drag.card.id)
          if (i !== -1) {
            from.cards.splice(i, 1)
            if (typeof from.total === 'number') from.total = Math.max(0, from.total - 1)
          }
        }
      }
    },

    // ─── Arrastre táctil (móvil/tablet) ─────────────────────────
    // El drag nativo de HTML5 no funciona con el dedo, así que emulamos
    // el arrastre con eventos touch: se activa con long-press (~280ms) y
    // reutiliza toda la lógica existente (onDrop → moveCard, modal de razón).
    onCardTouchStart(card, fromEtapa, e) {
      this.startCardTouch(card, fromEtapa, null, e)
    },
    // Long-press desde una columna-sección (origen = sección).
    onSectionCardTouchStart(card, col, e) {
      this.startCardTouch(card, null, col ? col.id : null, e)
    },
    startCardTouch(card, fromEtapa, fromSectionId, e) {
      if (this.selectMode) return
      if (!e.touches || e.touches.length !== 1) return
      const t = e.touches[0]
      this.touchDrag.card = card
      this.touchDrag.fromEtapa = fromEtapa
      this.touchDrag.fromSectionId = fromSectionId
      this.touchDrag.startX = t.clientX
      this.touchDrag.startY = t.clientY
      this.touchDrag.x = t.clientX
      this.touchDrag.y = t.clientY
      clearTimeout(this._touchTimer)
      this._touchTimer = setTimeout(() => {
        this.touchDrag.active = true
        this.dragged = { card, fromEtapa, fromSectionId }   // reutiliza el estado del DnD nativo
        if (navigator.vibrate) { try { navigator.vibrate(25) } catch (err) { /* noop */ } }
      }, 280)
    },
    onCardTouchMove(e) {
      const t = e.touches && e.touches[0]
      if (!t) return
      if (!this.touchDrag.active) {
        // Si el dedo se mueve antes de activar, es scroll: cancelar el long-press.
        const dx = Math.abs(t.clientX - this.touchDrag.startX)
        const dy = Math.abs(t.clientY - this.touchDrag.startY)
        if (dx > 10 || dy > 10) {
          clearTimeout(this._touchTimer)
          this.resetTouchDrag()
        }
        return
      }
      // Arrastre activo: mover el "fantasma" y detectar la columna destino
      // sin permitir que la página haga scroll.
      e.preventDefault()
      this.touchDrag.x = t.clientX
      this.touchDrag.y = t.clientY
      this._ptr = { x: t.clientX, y: t.clientY }
      this.refreshDropTarget(t.clientX, t.clientY)
      // Auto-scroll: horizontal (bordes del tablero) + vertical (bordes de la
      // columna bajo el dedo). Permite soltar en columnas/tarjetas fuera de vista.
      this.computeAutoScroll(t.clientX, t.clientY, true)
    },
    // Detecta la columna bajo un punto (etapa o columna-sección) y la resalta.
    // Ojo con las clases: esto vino del tablero viejo, que las llamaba
    // .kanban__col / .kanban__col-body. Acá las columnas son .mx-col y su
    // cuerpo .mx-col__b; con los nombres viejos el closest() no encontraba
    // nada nunca y el arrastre con el dedo no marcaba destino ni scrolleaba.
    refreshDropTarget(x, y) {
      const el = document.elementFromPoint(x, y)
      const col = el && el.closest ? el.closest('.mx-col') : null
      if (!col) return
      const secId = col.getAttribute('data-section-id')
      if (secId) {
        this.dragOverSectionId = Number(secId)
        this.dragOverEtapa = null
      } else {
        this.dragOverEtapa = col.getAttribute('data-etapa')
        this.dragOverSectionId = null
      }
    },
    // Compat: el arrastre táctil todavía llama a este nombre.
    updateDragOverEtapa() {
      this.refreshDropTarget(this.touchDrag.x, this.touchDrag.y)
    },
    // Calcula el desplazamiento automático según qué tan cerca del borde está el
    // puntero: horizontal mueve el tablero (izq/der), vertical mueve el cuerpo de
    // la columna que está debajo (arriba/abajo). Sirve para mouse y para táctil.
    computeAutoScroll(clientX, clientY, fromTouch = false) {
      const board = this.$refs.board
      if (!board) { this.stopAutoScroll(); return }
      // Con el dedo la franja útil tiene que ser más ancha y el desplazamiento
      // más rápido: la mano tapa la pantalla y no se apunta tan fino como con
      // el mouse. Además en celular cada columna ocupa casi todo el ancho, así
      // que hay más distancia que recorrer para llegar a la de al lado.
      const EDGE  = fromTouch ? 110 : 70
      const SPEED = fromTouch ? 34 : 18
      const rect = board.getBoundingClientRect()

      // La franja horizontal también se mide contra el ancho del tablero, igual
      // que la vertical: en un celular de 360px dos bordes fijos de 110 se comen
      // más de la mitad de la pantalla y el tablero arranca a correrse solo
      // apenas se levanta la tarjeta, sin lugar neutro para elegir columna.
      const EDGE_H = Math.max(40, Math.min(EDGE, Math.round(rect.width * 0.18)))

      let hDir = 0, hInt = 0
      if (clientX < rect.left + EDGE_H) {
        hDir = -1; hInt = Math.min(1, (rect.left + EDGE_H - clientX) / EDGE_H)
      } else if (clientX > rect.right - EDGE_H) {
        hDir = 1; hInt = Math.min(1, (clientX - (rect.right - EDGE_H)) / EDGE_H)
      }

      let vDir = 0, vInt = 0, vEl = null
      const under = document.elementFromPoint(clientX, clientY)
      const body = under && under.closest ? under.closest('.mx-col__b') : null
      if (body && body.scrollHeight > body.clientHeight + 1) {
        const b = body.getBoundingClientRect()
        // La franja vertical se mide contra el alto de la columna: en un celular
        // la lista es corta y un borde fijo de 110px dejaría casi todo dentro de
        // la zona activa, sin lugar neutro para apuntar a una tarjeta.
        const EDGE_V = Math.max(40, Math.min(EDGE, Math.round(b.height * 0.22)))
        if (clientY < b.top + EDGE_V) {
          vDir = -1; vInt = Math.min(1, (b.top + EDGE_V - clientY) / EDGE_V); vEl = body
        } else if (clientY > b.bottom - EDGE_V) {
          vDir = 1; vInt = Math.min(1, (clientY - (b.bottom - EDGE_V)) / EDGE_V); vEl = body
        }
      }

      this._autoScroll = { hDir, hInt, vDir, vInt, vEl, speed: SPEED }
      // Espejo reactivo del sentido horizontal, para encender la franja del
      // borde correspondiente durante el arrastre táctil.
      if (this.autoScrollHDir !== hDir) this.autoScrollHDir = hDir
      if (hDir !== 0 || vDir !== 0) this.startAutoScroll()
      else this.stopAutoScroll()
    },
    startAutoScroll() {
      if (this._autoScrollRAF) return
      const step = () => {
        const a = this._autoScroll
        const dragging = !!this.dragged || this.touchDrag.active
        if (!dragging || !a || (!a.hDir && !a.vDir)) { this._autoScrollRAF = null; return }
        const speed = a.speed || 18
        const board = this.$refs.board
        if (board && a.hDir) {
          board.scrollLeft += a.hDir * (6 + (a.hInt || 0) * speed)
        }
        if (a.vEl && a.vDir) {
          a.vEl.scrollTop += a.vDir * (6 + (a.vInt || 0) * speed)
        }
        // El puntero puede quedar quieto en el borde: recalcular la columna
        // destino (y disparar la carga de más tarjetas al llegar al fondo).
        if (this._ptr) this.refreshDropTarget(this._ptr.x, this._ptr.y)
        this._autoScrollRAF = requestAnimationFrame(step)
      }
      this._autoScrollRAF = requestAnimationFrame(step)
    },
    stopAutoScroll() {
      if (this._autoScrollRAF) { cancelAnimationFrame(this._autoScrollRAF); this._autoScrollRAF = null }
      this._autoScroll = { hDir: 0, hInt: 0, vDir: 0, vInt: 0, vEl: null, speed: 18 }
      if (this.autoScrollHDir !== 0) this.autoScrollHDir = 0
    },
    onCardTouchEnd() {
      clearTimeout(this._touchTimer)
      this.stopAutoScroll()
      if (this.touchDrag.active) {
        this._suppressClick = true            // evita abrir el chat tras soltar
        setTimeout(() => { this._suppressClick = false }, 400)
        if (this.dragOverSectionId) {
          // Soltó sobre una columna-sección → mover la conversación a esa sección.
          const col = this.renderedSectionColumns.find(c => c.id === this.dragOverSectionId)
          if (col) this.onDropSectionCol(col)
          else this.onDragEnd()
        } else if (this.dragOverEtapa) {
          this.onDrop(this.dragOverEtapa)      // misma lógica que el drop con mouse
        } else {
          this.onDragEnd()
        }
      }
      this.resetTouchDrag()
    },
    onCardTouchCancel() {
      clearTimeout(this._touchTimer)
      this.stopAutoScroll()
      if (this.touchDrag.active) this.onDragEnd()
      this.resetTouchDrag()
    },
    resetTouchDrag() {
      this.touchDrag = { active: false, card: null, fromEtapa: null, fromSectionId: null, x: 0, y: 0, startX: 0, startY: 0 }
    },
    onCardClick(card) {
      // Guarda: si venimos de un arrastre táctil, no dispares el click.
      if (this._suppressClick) { this._suppressClick = false; return }
      if (this.selectMode) this.toggleCardSelect(card)
      else this.openChat(card)
    },

    onDrop(toEtapa) {
      const drag = this.dragged
      this.dragOverEtapa = null
      this.dragOverSectionId = null
      if (!drag) return
      this.dragged = null

      // Si la tarjeta viene de una columna-sección y se suelta en una etapa,
      // se mueve a la sección principal (a la que pertenecen las etapas).
      if (drag.fromSectionId != null) {
        const principalId = this.currentSection && this.currentSection.id
        if (principalId) {
          this.removeDraggedFromOrigin(drag)
          this.$emit('move-to-section', { ids: [drag.card.id], sectionId: principalId })
        }
        return
      }

      if (drag.fromEtapa === toEtapa) return

      // Nadie que no sea super admin puede mover una tarjeta HACIA "Asignación":
      // si se suelta ahí, se cancela y la tarjeta vuelve a su columna de origen.
      if (this.isStageLocked(toEtapa)) {
        this.showToast('Solo un super admin puede mover conversaciones a Asignación.', 'error')
        return
      }

      // ¿La etapa destino exige razón? En modo sección lo define el flag
      // pedir_razon_fallo de la etapa; en legacy es la etapa 'intake_fallido'.
      const requiresReason = this.isSectionMode
        ? !!(this.etapas.find(e => e.key === toEtapa) || {}).pedir_razon_fallo
        : (toEtapa === 'intake_fallido')

      if (requiresReason) {
        // No se confirma (ni visual ni en backend) hasta que haya una razón.
        const toObj = this.etapas.find(e => e.key === toEtapa) || {}
        this.intakeModal = { open: true, card: drag.card, fromEtapa: drag.fromEtapa, toEtapa: toEtapa, toLabel: toObj.label || 'Intake fallido', razon: '', error: '' }
        this.$nextTick(() => { if (this.$refs.razonInput) this.$refs.razonInput.focus() })
        return
      }

      this.moveCard(drag.card, drag.fromEtapa, toEtapa, null)
    },

    confirmIntake() {
      const razon = (this.intakeModal.razon || '').trim()
      if (!razon) {
        this.intakeModal.error = 'La razón es obligatoria.'
        return
      }
      const { card, fromEtapa, toEtapa } = this.intakeModal
      this.intakeModal.open = false
      this.moveCard(card, fromEtapa, toEtapa || 'intake_fallido', razon)
    },
    cancelIntake() {
      // Cancela el movimiento por completo: la tarjeta queda donde estaba.
      this.intakeModal = { open: false, card: null, fromEtapa: null, toEtapa: null, razon: '', error: '' }
    },

    async moveCard(card, fromEtapa, toEtapa, razon) {
      const from = this.columns[fromEtapa]
      const to = this.columns[toEtapa]
      if (!from || !to) return
      const idx = from.cards.findIndex(c => c.id === card.id)
      if (idx === -1) return

      // Movimiento optimista
      const [moved] = from.cards.splice(idx, 1)
      const prevEtapa = moved.etapa
      const prevStageId = moved.stage_id
      if (this.isSectionMode) {
        this.$set(moved, 'stage_id', to.stage_id)
      } else {
        this.$set(moved, 'etapa', toEtapa)
      }
      to.cards.unshift(moved)

      // La tarjeta recién movida NO está atrasada: reseteamos su reloj de etapa
      // para que al terminar el festejo no reaparezca el gif/mensaje del estado
      // anterior (bug de "vuelve a aparecer lo que tenía antes").
      this.$set(moved, 'last_moved_at', new Date().toISOString())

      // Festejando: aparece 1 sola vez al mover de columna y luego desaparece.
      this.celebrateMove(moved.id)

      // Mantener el total REAL de cada columna en sincronía con el movimiento.
      if (typeof from.total === 'number') from.total = Math.max(0, from.total - 1)
      if (typeof to.total === 'number') to.total += 1

      try {
        const body = {}
        if (this.isSectionMode) {
          body.stage_id = to.stage_id
        } else {
          body.etapa = toEtapa
        }
        if (razon) body.razon = razon
        const changedBy = Number(localStorage.getItem('idusuario'))
        if (changedBy) body.changed_by = changedBy

        const res = await fetch(`${apiUrls.conversationsSupport}/${moved.id}/stage`, {
          method: 'PATCH',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: this.token()
          },
          body: JSON.stringify(body)
        })
        if (!res.ok) {
          let msg = 'No se pudo cambiar la etapa.'
          try { const j = await res.json(); if (j && j.error) msg = j.error } catch (e) { /* noop */ }
          throw new Error(msg)
        }
        const json = await res.json()
        if (json && json.data) Object.assign(moved, json.data)
        // El endpoint /stage devuelve last_moved_at = created_at (fallback), que
        // reactivaría el SLA de la tarjeta recién movida. Lo mantenemos en "ahora".
        this.$set(moved, 'last_moved_at', new Date().toISOString())
        // Refresco silencioso: deja en vivo posición, gifs, textos y contadores
        // con los datos reales del servidor, sin apretar "Refrescar".
        this.fetchBoard({ silent: true })
      } catch (err) {
        // Revertir el movimiento
        const ti = to.cards.findIndex(c => c.id === moved.id)
        if (ti !== -1) to.cards.splice(ti, 1)
        if (this.isSectionMode) {
          this.$set(moved, 'stage_id', prevStageId)
        } else {
          this.$set(moved, 'etapa', prevEtapa)
        }
        from.cards.splice(idx, 0, moved)

        // Revertir también el ajuste de totales.
        if (typeof to.total === 'number') to.total = Math.max(0, to.total - 1)
        if (typeof from.total === 'number') from.total += 1

        // Cancelar el festejando si el movimiento falló.
        if (this.celebrateCardId === moved.id) {
          if (this._celebrateTimer) clearTimeout(this._celebrateTimer)
          this.celebrateCardId = null
          this._celebrateTimer = null
        }

        this.showToast(err.message || 'No se pudo cambiar la etapa.', 'error')
      }
    },

    openChat(card) {
      // Reusa la lógica de apertura de chat del dashboard padre.
      this.$emit('open-chat', card)
    },

    // ─── Selección múltiple + mover a sección ───────────────────
    toggleSelectMode() {
      this.selectMode = !this.selectMode
      this.selectedCardIds = []
      this.showMovePicker = false
    },
    isCardSelected(id) {
      return this.selectedCardIds.map(String).includes(String(id))
    },
    toggleCardSelect(card) {
      if (!card || !card.id) return
      if (this.isCardSelected(card.id)) {
        this.selectedCardIds = this.selectedCardIds.filter(x => String(x) !== String(card.id))
      } else {
        this.selectedCardIds = [...this.selectedCardIds, card.id]
      }
    },
    moveSelectedToSection(sec) {
      if (!sec || !sec.id || !this.selectedCardIds.length) return
      this.$emit('move-to-section', { ids: this.selectedCardIds.slice(), sectionId: sec.id })
      this.selectMode = false
      this.selectedCardIds = []
      this.showMovePicker = false
    },

    // ─── Helpers ────────────────────────────────────────────────
    // ¿Esta etapa es la columna "Asignación"? Matchea "Asignación"/"Asignacion"
    // (no "Asignado" ni "Sin asignar", que no empiezan por "asignaci").
    isAssignmentEtapa(etapa) {
      return this.normalizeStr(etapa && etapa.label).indexOf('asignaci') === 0
    },
    // ¿Esta etapa está bloqueada como DESTINO? Solo un super admin (rol asignado
    // en /admin/permisos → prop isSuperAdmin) puede mover una conversación HACIA
    // "Asignación". Sacarlas de ahí lo puede hacer cualquiera: la columna es el
    // punto de partida del flujo y frenaba el trabajo del equipo.
    isStageLocked(etapaKey) {
      if (this.isSuperAdmin) return false
      const etapa = this.etapas.find(e => e.key === etapaKey)
      return this.isAssignmentEtapa(etapa)
    },
    normalizeStr(s) {
      return s ? String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') : ''
    },
    // Key de búsqueda de una columna-sección (para no chocar con los slugs de etapa).
    sectionColKey(col) {
      return 'sec:' + (col ? col.id : '')
    },
    // ¿Esta columna está mostrando un subconjunto (búsqueda activa o tag)? Se usa
    // para mostrar el conteo de resultados en vez del total real.
    isColFiltered(key) {
      if (this.tagFilter) return true
      if (String(this.globalSearch).trim()) return true
      return this.colSearch.key === key && !!this.colSearch.query
    },
    // ¿La tarjeta pasa el filtro de tag/origen (y de codigo_ads si aplica)?
    matchesTag(card) {
      if (!this.tagFilter) return true
      if (!card || card.lead_origin !== this.tagFilter) return false
      if (this.tagFilter === 'meta' && this.adsCodeFilter) {
        return String(card.lead_ads_code || '') === String(this.adsCodeFilter)
      }
      return true
    },
    // ¿La tarjeta casa con el texto buscado? Cada palabra tiene que aparecer en
    // el nombre de la conversación (sin acentos, así que cubre nombre y
    // apellido) o en el teléfono (solo dígitos). Al pedir todas las palabras,
    // "perez juan" y "juan 600" también encuentran.
    matchesQuery(card, rawQuery) {
      const query = this.normalizeStr(rawQuery || '').trim()
      if (!query) return true

      const name = this.normalizeStr(card.client_name || card.client_identifier || '')
      const phoneDigits = String(this.cardPhone(card) || '').replace(/\D/g, '')

      return query.split(/\s+/).every(token => {
        if (token && name.indexOf(token) !== -1) return true
        const digits = token.replace(/\D/g, '')
        return !!digits && phoneDigits.indexOf(digits) !== -1
      })
    },
    // Aplica a una lista de tarjetas el filtro de tag, la búsqueda global del
    // tablero y, si la búsqueda está abierta en esa columna, su texto buscado.
    filterCards(cards, key) {
      const list = cards || []
      const colQuery = (this.colSearch.key === key) ? String(this.colSearch.query).trim() : ''
      const globalQuery = String(this.globalSearch).trim()
      if (!this.tagFilter && !colQuery && !globalQuery) return list

      const index = this.dedupeIndex
      return list.filter(c => {
        if (!this.matchesTag(c)) return false
        if (!this.matchesQuery(c, globalQuery)) return false
        if (!this.matchesQuery(c, colQuery)) return false
        // Del mismo lead se muestra una sola tarjeta (ver dedupeIndex).
        const entry = index.get(this.dedupeKey(c))
        if (entry && entry.count > 1 && entry.winner !== c) return false
        return true
      })
    },
    // ── Deduplicado por lead ──
    // La regla del dominio es una conversación por lead (ver
    // IntakeSectionConversationService): si el mismo teléfono quedó con filas en
    // varias secciones, al buscar aparecía la misma persona en cada columna.
    // Clave del lead: el teléfono en dígitos; si no hay, el nombre normalizado.
    dedupeKey(card) {
      if (!card) return ''
      const digits = String(this.cardPhone(card) || '').replace(/\D/g, '')
      if (digits.length >= 6) return 'p:' + digits
      const name = this.normalizeStr(card.client_name || '')
      return name ? 'n:' + name : ''
    },
    // ¿`a` le gana a `b` como tarjeta a conservar? Manda tener agente asignado;
    // después la actividad más reciente y, si empatan, el id más alto.
    dedupeBeats(a, b) {
      const aAgent = a && a.assigned_agent ? 1 : 0
      const bAgent = b && b.assigned_agent ? 1 : 0
      if (aAgent !== bAgent) return aAgent > bAgent

      const at = Date.parse(a.last_client_message_at || '') || 0
      const bt = Date.parse(b.last_client_message_at || '') || 0
      if (at !== bt) return at > bt

      const an = Number(a.id)
      const bn = Number(b.id)
      if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an > bn
      return String(a.id) > String(b.id)
    },
    // Cuántas copias del mismo lead quedaron ocultas detrás de esta tarjeta.
    hiddenDuplicates(card) {
      const entry = this.dedupeIndex.get(this.dedupeKey(card))
      return (entry && entry.winner === card) ? entry.count - 1 : 0
    },
    // Tarjetas visibles de una etapa (columna de stage).
    visibleCards(etapa) {
      const col = this.columns[etapa.key]
      return this.filterCards(col && col.cards, etapa.key)
    },
    // Tarjetas visibles de una columna-sección.
    visibleSectionCards(col) {
      return this.filterCards(col && col.cards, this.sectionColKey(col))
    },
    toggleColSearch(key) {
      if (this.colSearch.key === key) { this.closeColSearch(); return }
      this.colSearch.key = key
      this.colSearch.query = ''
      // Traer todas las páginas de la columna para que la búsqueda cubra el total,
      // no solo las tarjetas ya cargadas por scroll.
      this.loadAllForColumn(key)
      this.$nextTick(() => {
        const el = this.$refs.colSearchInput
        const input = Array.isArray(el) ? el[0] : el
        if (input && input.focus) input.focus()
      })
    },
    // Carga secuencialmente todas las páginas restantes de una columna. Acepta
    // el slug de una etapa o la key 'sec:<id>' de una columna-sección.
    async loadAllForColumn(key) {
      // Generación del tablero al empezar: si cambia (se pasó a otra sección) se
      // corta el paginado, así no se sigue llenando el tablero nuevo.
      const seq = this.boardSeq
      if (String(key).indexOf('sec:') === 0) {
        const id = String(key).slice(4)
        let guardS = 0
        while (guardS < 100 && seq === this.boardSeq) {
          const col = this.sectionColumns.find(sc => String(sc.id) === id)
          if (!col || !col.nextCursor) return
          guardS++
          await this.fetchMoreSection(col)
        }
        return
      }
      let guard = 0
      while (guard < 100 && seq === this.boardSeq) {
        const col = this.columns[key]
        if (!col || !col.nextCursor) return
        guard++
        await this.fetchMore(key)
      }
    },
    // Trae todas las páginas de todas las columnas (etapas + secciones) para que
    // el filtro por tag se aplique sobre el tablero completo.
    async loadAllColumns() {
      if (this.tagLoading) return
      const seq = this.boardSeq
      this.tagLoading = true
      try {
        const keys = this.etapas.map(e => e.key)
          .concat(this.renderedSectionColumns.map(c => this.sectionColKey(c)))
        await Promise.all(keys.map(k => this.loadAllForColumn(k)))
      } finally {
        // Si el tablero cambió, la bandera ya la reseteó resetBoardFilters y
        // puede haber otra carga en curso: no pisarla.
        if (seq === this.boardSeq) this.tagLoading = false
      }
    },
    setTagFilter(value) {
      this.tagFilter = (this.tagFilter === value) ? null : value
    },

    // ─── Filtro por rango de fechas (last_move_seccion) ─────────
    // Fecha local (la del navegador, que en la oficina es hora de Chile) en el
    // formato YYYY-MM-DD que esperan el <input type="date"> y el backend.
    localDate(date) {
      const d = new Date(date)
      const mes = String(d.getMonth() + 1).padStart(2, '0')
      const dia = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mes}-${dia}`
    },
    // 'YYYY-MM-DD' → 'dd/mm' para el botón.
    shortDate(iso) {
      const [, mes, dia] = String(iso).split('-')
      return (dia && mes) ? `${dia}/${mes}` : iso
    },
    // Rango { desde, hasta } de cada atajo.
    presetRange(key) {
      const hoy = new Date()
      switch (key) {
        case 'hoy': {
          const d = this.localDate(hoy)
          return { desde: d, hasta: d }
        }
        case 'ayer': {
          const ayer = new Date(hoy)
          ayer.setDate(ayer.getDate() - 1)
          const d = this.localDate(ayer)
          return { desde: d, hasta: d }
        }
        case 'semana': {
          const inicio = new Date(hoy)
          // Hoy incluido: hoy y los 6 días anteriores.
          inicio.setDate(inicio.getDate() - 6)
          return { desde: this.localDate(inicio), hasta: this.localDate(hoy) }
        }
        case 'mes': {
          const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
          return { desde: this.localDate(inicio), hasta: this.localDate(hoy) }
        }
        default:
          return { desde: '', hasta: '' }
      }
    },
    toggleDateMenu() {
      // Al abrir, el menú arranca mostrando el rango que está aplicado.
      if (!this.dateMenuOpen) this.draftDates = { ...this.dateFilter }
      this.dateMenuOpen = !this.dateMenuOpen
    },
    applyDatePreset(key) {
      this.draftDates = this.presetRange(key)
      this.applyDates()
    },
    // Aplica el rango elegido y vuelve a pedir el tablero: el filtro es del lado
    // del backend, así que los totales de cada columna llegan ya filtrados.
    applyDates() {
      if (!this.draftDates.desde && !this.draftDates.hasta) return
      this.dateFilter = { ...this.draftDates }
      this.dateMenuOpen = false
      this.fetchBoard()
    },
    clearDateFilter() {
      const tenia = this.hasDateFilter
      this.dateFilter = { desde: '', hasta: '' }
      this.draftDates = { desde: '', hasta: '' }
      this.dateMenuOpen = false
      if (tenia) this.fetchBoard()
    },
    // Suma ?desde/?hasta a las llamadas al tablero (carga inicial y scroll).
    setDateParams(params) {
      if (this.dateFilter.desde) params.set('desde', this.dateFilter.desde)
      if (this.dateFilter.hasta) params.set('hasta', this.dateFilter.hasta)
      return params
    },
    // Cierra el menú de fechas al hacer clic fuera.
    onDocClickDates(evt) {
      if (!this.dateMenuOpen) return
      if (evt.target && evt.target.closest && evt.target.closest('.kanban__dates')) return
      this.dateMenuOpen = false
    },
    closeColSearch() {
      this.colSearch.key = null
      this.colSearch.query = ''
    },
    /**
     * Deja el tablero listo para otra sección.
     *
     * Los filtros de la BARRA SUPERIOR (búsqueda, origen y rango de fechas) son
     * globales: no se limpian al cambiar de sección, se vuelven a tomar de las
     * props. Si se limpiaran por dentro, los chips de arriba seguirían puestos
     * mostrando un filtro que el tablero ya no aplica.
     *
     * Lo que sí se limpia es lo que pertenecía al tablero anterior: la búsqueda
     * por columna, la selección múltiple y las cargas en curso.
     */
    resetBoardFilters() {
      clearTimeout(this._globalSearchTimer)
      this.globalSearch = this.query || ''
      this.globalSearchLoading = false
      this.tagLoading = false
      this.closeColSearch()
      this.tagFilter = this.origin || null
      this.adsCodeFilter = ''
      this.dateFilter = { desde: this.desde || '', hasta: this.hasta || '' }
      this.draftDates = { ...this.dateFilter }
      this.dateMenuOpen = false
      this.selectMode = false
      this.selectedCardIds = []
      this.showMovePicker = false
    },
    columnCountLabel(etapaKey) {
      const col = this.columns[etapaKey]
      if (!col) return '0'
      // Total REAL de chats de la etapa cuando lo trae el backend; si no,
      // fallback al total cargado en el listado.
      if (typeof col.total === 'number') return String(col.total)
      return col.nextCursor ? `${col.cards.length}+` : String(col.cards.length)
    },
    sectionColCountLabel(col) {
      if (!col) return '0'
      if (typeof col.total === 'number') return String(col.total)
      return col.nextCursor ? `${col.cards.length}+` : String(col.cards.length)
    },
    // ─── Motivo de la columna (columnas con "pedir razón") ──────
    // Los movimientos automáticos llegan con una razón puesta por el workflow
    // ("n8n: primer-contacto/Mover stage"): es la traza del bot, no un motivo
    // que le sirva a nadie mirando el tablero, así que la tratamos como vacía.
    // Solo se muestra lo que escribió una persona.
    humanReason(card) {
      const r = String((card && card.stage_reason && card.stage_reason.razon) || '').trim()
      return /^n8n\b/i.test(r) ? '' : r
    },
    // En una columna que pide razón el botón se muestra siempre; en el resto
    // (por ejemplo las columnas-sección) solo si hay un motivo registrado para
    // la etapa en la que está hoy la conversación.
    showReasonBtn(card, etapa) {
      if (etapa && etapa.pedir_razon_fallo) return true
      return !!this.humanReason(card)
    },
    // Nombre de la columna a mostrar en el botón y en el pop-up.
    reasonStageLabel(card, etapa) {
      if (etapa && etapa.label) return etapa.label
      const reason = card && card.stage_reason
      return (reason && reason.stage_name) || 'Motivo'
    },
    openReasonModal(card, etapa) {
      const reason = (card && card.stage_reason) || {}
      this.reasonModal = {
        open: true,
        name: card.client_name || card.client_identifier || 'Sin nombre',
        stage: this.reasonStageLabel(card, etapa),
        reason: this.humanReason(card),
        by: reason.by || '',
        at: reason.at || '',
      }
    },
    closeReasonModal() {
      this.reasonModal = { open: false, name: '', stage: '', reason: '', by: '', at: '' }
    },
    formatReasonDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d)) return ''
      return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    cardPhone(card) {
      return card.telefono || card.client_identifier || card.client_phone || ''
    },
    // ¿El teléfono del contacto (dato del intake) está completo? Un número
    // internacional válido (con o sin '+') tiene al menos 10 dígitos, ej:
    // +34600123456. Fragmentos como '+3468' o '456' se consideran incompletos.
    isCardPhoneComplete(card) {
      const digits = String(this.cardPhone(card) || '').replace(/\D/g, '')
      return digits.length >= 10
    },
    statusLabel(s) { return STATUS_LABELS[s] || s },
    // Badge de origen del lead (según codigo_ads del intake, resuelto en el back).
    // Si no se puede determinar (lead_origin nulo), no se muestra badge.
    originBadge(card) {
      const origin = card && card.lead_origin
      if (!origin) return null
      // Meta: en vez de decir "Meta" a secas, se muestra el codigo_ads del intake
      // (la campaña de la que vino el lead). Si no hay código, cae a "Meta".
      if (origin === 'meta') {
        const code = String((card && card.lead_ads_code) || '').trim()
        return {
          label: code || 'Meta',
          cls: 'kanban__badge--origin-meta',
          title: code ? `Meta · campaña ${code}` : 'Meta',
        }
      }
      const map = {
        erasmusplay: { label: 'ErasmusPlay', cls: 'kanban__badge--origin-erasmus', title: 'Lead de ErasmusPlay' },
        directo:     { label: 'Directo',     cls: 'kanban__badge--origin-directo', title: 'Lead directo de la web' },
      }
      return map[origin] || null
    },
    channelIcon(ch) {
      if (ch === 'whatsapp') return '🟢'
      if (ch === 'instagram') return '🟣'
      if (ch === 'web') return '🌐'
      return '💬'
    },
    initials(name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/).slice(0, 2)
      return parts.map(p => p.charAt(0).toUpperCase()).join('') || '?'
    },
    relativeTime(iso) {
      if (!iso) return ''
      const then = new Date(iso).getTime()
      if (isNaN(then)) return ''
      const diff = Math.floor((Date.now() - then) / 1000)
      if (diff < 60) return 'ahora'
      if (diff < 3600) return `${Math.floor(diff / 60)}m`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`
      if (diff < 604800) return `${Math.floor(diff / 86400)}d`
      const d = new Date(iso)
      return `${d.getDate()}/${d.getMonth() + 1}`
    },
    // Tiempo transcurrido desde el último movimiento hasta ahora (tiempo en la
    // etapa actual). Usa last_moved_at (o created_at como fallback). Se refresca
    // con nowTick para que el número vaya creciendo solo.
    stageElapsed(card) {
      const iso = card.last_moved_at || card.created_at
      if (!iso) return ''
      const then = new Date(iso).getTime()
      if (isNaN(then)) return ''
      let s = Math.floor((this.nowTick - then) / 1000)
      if (s < 0) s = 0
      if (s < 60) return 'recién'
      const d = Math.floor(s / 86400)
      const h = Math.floor((s % 86400) / 3600)
      const m = Math.floor((s % 3600) / 60)
      if (d > 0) return `${d}d ${h}h`
      if (h > 0) return `${h}h ${m}m`
      return `${m}m`
    },
    // Formatea una duración en segundos como "Xd Yh" / "Yh Zm" / "Zm" / "Xs".
    formatDurationSecs(secs) {
      if (secs == null || isNaN(secs)) return null
      let s = Math.max(0, Math.floor(secs))
      if (s < 60) return `${s}s`
      const d = Math.floor(s / 86400)
      const h = Math.floor((s % 86400) / 3600)
      const m = Math.floor((s % 3600) / 60)
      if (d > 0) return `${d}d ${h}h`
      if (h > 0) return `${h}h ${m}m`
      return `${m}m`
    },
    // Tiempo de primer contacto de una tarjeta (o null si aún sin contactar).
    firstContactLabel(card) {
      return this.formatDurationSecs(card && card.first_contact_seconds)
    },
    // Promedio de primer contacto sobre las tarjetas VISIBLES de la columna.
    avgFirstContact(cards) {
      const vals = (cards || [])
        .map(c => (c ? c.first_contact_seconds : null))
        .filter(v => typeof v === 'number' && !isNaN(v))
      if (!vals.length) return null
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length
      return this.formatDurationSecs(avg)
    },
    columnFirstContactAvg(etapa) {
      return this.avgFirstContact(this.visibleCards(etapa))
    },
    sectionFirstContactAvg(col) {
      return this.avgFirstContact(this.visibleSectionCards(col))
    },
    // ── Alerta de SLA por tiempo en la etapa ──
    // La alerta depende del TIEMPO que la tarjeta lleva SIN MOVERSE (last_moved_at,
    // o created_at como fallback), NO de un umbral fijo por columna. Escala sola:
    //   > 12h → preocupado · > 48h → semienojado · > 72h → enojado.
    // Solo se muestra en las 3 PRIMERAS columnas del tablero (etapaIndex < 3).

    // Horas transcurridas desde el último movimiento hasta ahora (nowTick).
    elapsedHours(card) {
      const iso = card && (card.last_moved_at || card.created_at)
      if (!iso) return 0
      const then = new Date(iso).getTime()
      if (isNaN(then)) return 0
      const h = (this.nowTick - then) / 3600000
      return h > 0 ? h : 0
    },
    // Umbral de SLA alcanzado según las horas transcurridas (72 → 48 → 12).
    // Devuelve 0 si todavía no llegó a 12h.
    slaThresholdReached(card) {
      const h = this.elapsedHours(card)
      if (h >= 72) return 72
      if (h >= 48) return 48
      if (h >= 12) return 12
      return 0
    },
    // Solo las 3 primeras columnas miden SLA.
    isAlertColumn(etapaIndex) {
      return etapaIndex != null && etapaIndex < 3
    },
    slaAlert(card, etapaIndex) {
      if (!this.isAlertColumn(etapaIndex)) return false
      return this.slaThresholdReached(card) > 0
    },
    slaMessage(card, etapaIndex) {
      if (!this.isAlertColumn(etapaIndex)) return ''
      const th = this.slaThresholdReached(card)
      const msgs = {
        12: 'Hace más de 12 h que no se mueve.',
        48: 'Hace más de 48 h que no se mueve.',
        72: 'Más de 72 h en la misma etapa.',
      }
      return msgs[th] || ''
    },
    // Marca una tarjeta como recién movida por unos segundos (la resalta).
    celebrateMove(cardId) {
      if (!cardId) return
      if (this._celebrateTimer) clearTimeout(this._celebrateTimer)
      this.celebrateCardId = cardId
      this._celebrateTimer = setTimeout(() => {
        this.celebrateCardId = null
        this._celebrateTimer = null
      }, 2500)
    },
    showToast(message, type = 'error') {
      if (this.toast.timer) clearTimeout(this.toast.timer)
      this.toast = { open: true, type, message, timer: null }
      this.toast.timer = setTimeout(() => { this.toast.open = false }, 4000)
    },

    // ─── Gestión de columnas (conversation_stage) — solo admins ──
    stagesUrl(slug) {
      const base = `${apiUrls.base}support/stages`
      return slug ? `${base}/${encodeURIComponent(slug)}` : base
    },
    openCreateStage() {
      const current = this.currentSection && this.currentSection.id
      this.stageModal = {
        open: true,
        mode: 'create',
        slug: null,
        nombre: '',
        color: '#B2BEC3',
        pedirRazon: false,
        // Por defecto marcamos la sección actual.
        sectionIds: current != null ? [current] : [],
        error: '',
        saving: false,
      }
      this.$nextTick(() => { if (this.$refs.stageNameInput) this.$refs.stageNameInput.focus() })
    },
    async openEditStage(etapa) {
      if (!etapa) return
      this.stageModal = {
        open: true,
        mode: 'edit',
        slug: etapa.key,
        nombre: etapa.label || '',
        color: etapa.color || '#B2BEC3',
        pedirRazon: !!etapa.pedir_razon_fallo,
        sectionIds: [],
        sectionOptions: [],
        loadingSections: true,
        error: '',
        saving: false,
      }
      this.$nextTick(() => { if (this.$refs.stageNameInput) this.$refs.stageNameInput.focus() })

      // Traer las secciones que tienen esta columna y pre-seleccionar SOLO la
      // sección actual, para que por defecto el cambio no afecte a las demás.
      await this.loadStageSectionsForEdit(etapa.key)
      if (this.stageModal.mode !== 'edit' || this.stageModal.slug !== etapa.key) return // el modal cambió mientras cargaba
      const optIds = this.stageModal.sectionOptions.map(o => o.id)
      const curId = (this.currentSection && this.currentSection.id != null) ? Number(this.currentSection.id) : null
      this.stageModal.sectionIds = (curId != null && optIds.includes(curId)) ? [curId] : optIds.slice()
    },
    // Carga (desde el índice de columnas) las secciones que tienen la columna
    // `slug`, mapeadas a las secciones conocidas para mostrarlas con checkbox.
    async loadStageSectionsForEdit(slug) {
      try {
        const res = await fetch(this.stagesUrl(null), {
          headers: { Accept: 'application/json', Authorization: this.token() },
        })
        const json = await res.json().catch(() => ({}))
        const groups = (json && json.data) || []
        const group = groups.find(g => String(g.slug) === String(slug))
        const ids = group && Array.isArray(group.section_ids) ? group.section_ids.map(Number) : []
        const byId = new Map(
          (this.sections || []).filter(s => s && s.id != null).map(s => [Number(s.id), s])
        )
        const options = ids
          .map(id => byId.get(Number(id)))
          .filter(Boolean)
          .map(s => ({ id: Number(s.id), label: s.label }))
        this.stageModal.sectionOptions = options.length ? options : this.currentSectionOption()
      } catch (e) {
        this.stageModal.sectionOptions = this.currentSectionOption()
      } finally {
        this.stageModal.loadingSections = false
      }
    },
    // Fallback: la sección actual como única opción.
    currentSectionOption() {
      const s = this.currentSection
      return (s && s.id != null) ? [{ id: Number(s.id), label: s.label }] : []
    },
    openDeleteStage(etapa) {
      if (!etapa) return
      this.stageModal = {
        open: true,
        mode: 'delete',
        slug: etapa.key,
        nombre: etapa.label || '',
        color: etapa.color || '#B2BEC3',
        pedirRazon: !!etapa.pedir_razon_fallo,
        sectionIds: [],
        error: '',
        saving: false,
      }
    },
    closeStageModal() {
      this.stageModal.open = false
      this.stageModal.mode = null
      this.stageModal.error = ''
      this.stageModal.saving = false
    },
    toggleStageSection(id) {
      const i = this.stageModal.sectionIds.indexOf(id)
      if (i === -1) this.stageModal.sectionIds.push(id)
      else this.stageModal.sectionIds.splice(i, 1)
    },
    async saveStage() {
      const m = this.stageModal
      if (m.saving) return

      let method = 'POST'
      let url = this.stagesUrl(null)
      let body = null

      if (m.mode === 'create') {
        const nombre = (m.nombre || '').trim()
        if (!nombre) { m.error = 'Ingresá un nombre para la columna.'; return }
        if (!m.sectionIds.length) { m.error = 'Elegí al menos una sección.'; return }
        body = {
          nombre,
          color: m.color,
          section_ids: m.sectionIds.slice(),
          pedir_razon_fallo: m.pedirRazon,
        }
      } else if (m.mode === 'edit') {
        const nombre = (m.nombre || '').trim()
        if (!nombre) { m.error = 'Ingresá un nombre para la columna.'; return }
        if (!m.sectionIds.length) { m.error = 'Elegí al menos una sección donde aplicar el cambio.'; return }
        method = 'PATCH'
        url = this.stagesUrl(m.slug)
        body = {
          nombre,
          color: m.color,
          pedir_razon_fallo: m.pedirRazon,
          section_ids: m.sectionIds.slice(),
        }
      } else if (m.mode === 'delete') {
        method = 'DELETE'
        url = this.stagesUrl(m.slug)
      } else {
        return
      }

      m.saving = true
      m.error = ''
      try {
        const opts = {
          method,
          headers: { Accept: 'application/json', Authorization: this.token() },
        }
        if (body) {
          opts.headers['Content-Type'] = 'application/json'
          opts.body = JSON.stringify(body)
        }
        const res = await fetch(url, opts)
        if (!res.ok) {
          let msg = 'No se pudo guardar la columna.'
          try { const j = await res.json(); if (j && j.error) msg = j.error } catch (e) { /* noop */ }
          throw new Error(msg)
        }
        this.closeStageModal()
        const okMsg = m.mode === 'create' ? 'Columna creada.'
          : m.mode === 'delete' ? 'Columna eliminada.'
            : 'Columna actualizada.'
        this.showToast(okMsg, 'success')
        this.$emit('stages-changed')
        await this.fetchBoard()
      } catch (err) {
        m.error = err.message || 'No se pudo guardar la columna.'
      } finally {
        m.saving = false
      }
    },
  }
}
</script>

<style src="./mx.css"></style>
